import { useEffect, useState } from 'react';
import { s } from './Pattern.styles';
import { SidoList } from '../../../search/FilterData';
import YearRangeSliderModal, { getSubLabel } from './YearRangeSliderModal';
import { getRangeLabel } from './YearRangeSliderModal';

import { usePatternContext } from '../../../../context/PatternContext';
import { isLogin, REGION_CODE_MAP } from '../../../../utils/auth';
import ItemCategoryModal from './ItemCategoryModal';
import ItemDetailModal from './ItemDetailModal';
import { useNavigate } from 'react-router-dom';
import UseAreaModal from './UseAreaModal';
import { useUser } from '../../../../context/UserContext';
import { investmentPeriodOptions, investmentPurposeOptions } from '../data';
import RegionSelectModal from '../../../components/filterModal/RegionSelectModal';
import SelectedOptions from './SelectedOptions';

const AnalysisCriteria = () => {
  const { user } = useUser();
  const { investOptions, setInvestOptions, setAvailable } = usePatternContext();

  const [isOpen, setIsOpen] = useState(true);

  const [regionModalOn, setRegionModalOn] = useState(false);
  const [categoryModalOn, setCategoryModalOn] = useState(false);
  const [detailModalOn, setDetailModalOn] = useState(false);

  const [isUseAreaModalOpen, setIsUseAreaModalOpen] = useState(false);
  const [isYearModalOpen, setIsYearModalOpen] = useState(false);

  const [userSidoList, setUserSidoList] = useState<
    {
      code: string;
      region: string;
    }[]
  >([]);
  const [disableList, setDisableList] = useState<
    {
      code: string;
      region: string;
    }[]
  >([]);

  useEffect(() => {
    if (!isLogin() || !user) {
      setUserSidoList(SidoList);

      return;
    }

    const grade = user?.plan_grade;

    if (grade === 'P' || grade === 'T' || !grade) {
      setUserSidoList(SidoList);
      return;
    }

    const planRegion = user.plan_region ?? 'A';
    const regions = planRegion.split(',');

    const allowed = new Set<string>();

    regions.forEach((r: string) => {
      if (r === 'A') {
        SidoList.forEach((sido) => allowed.add(sido.region));
      } else if (r === 'F') {
        SidoList.forEach((sido) => {
          if (!['서울', '경기', '인천'].includes(sido.region)) {
            allowed.add(sido.region);
          }
        });
      } else {
        REGION_CODE_MAP[r]?.forEach((region) => allowed.add(region));
      }
    });

    const { allowedList, blockedList } = SidoList.reduce(
      (acc, sido) => {
        if (allowed.has(sido.region)) acc.allowedList.push(sido);
        else acc.blockedList.push(sido);
        return acc;
      },
      {
        allowedList: [] as typeof SidoList,
        blockedList: [] as typeof SidoList,
      },
    );

    const prioritized = [...allowedList, ...blockedList];
    setDisableList(blockedList);
    setUserSidoList(prioritized);
  }, [user]);

  const togglePurpose = (value: string) => {
    const now = investOptions.investmentPurpose;

    setInvestOptions({
      ...investOptions,
      investmentPurpose: now === value ? 'A' : value,
      aging_start_year: now === value ? '' : '1980',
      aging_end_year: now === value ? '' : '2025',
    });
  };

  const handleYearApply = (range: { start: string; end: string }) => {
    setInvestOptions((prev) => ({
      ...prev,
      aging_start_year: range.start,
      aging_end_year: range.end,
    }));
  };

  const handleChangeRegion = (r: {
    pnu: string;
    sido: string;
    sigungu: string;
    emd: string;
  }) => {
    setInvestOptions((prev) => ({
      ...prev,
      pnu: r.pnu,
      region: {
        sido: r.sido,
        sigungu: r.sigungu,
        emd: r.emd,
      },
    }));
  };

  const showAgingYear = investOptions.investmentPurpose !== 'A';
  const showUsageAreaBlock = investOptions.investmentPurpose === 'C';

  useEffect(() => {
    if (investOptions.pnu && investOptions.category) {
      setAvailable(true);
    } else {
      setAvailable(false);
    }
  }, [investOptions]);

  return (
    <s.PatternBox>
      <s.SelectHeader onClick={() => setIsOpen((prev) => !prev)}>
        <s.SelectTitleWrapper>
          <s.SelectTitle>투자 알고리즘</s.SelectTitle>
          <s.SelectHeaderStep>Step 1.</s.SelectHeaderStep>
        </s.SelectTitleWrapper>
        <s.ArrowButton
          $isOpen={isOpen}
          src={'/assets/images/arrow/arrow-up-black.svg'}
          alt=""
        />
      </s.SelectHeader>
      {isOpen && (
        <>
          <s.Divider />
          <s.SelectMain>
            <s.Column>
              <s.Block>
                <s.BlockLabel>
                  <span>투자 지역</span>
                </s.BlockLabel>
                <s.BlockContent>
                  <div style={{ width: '100%' }}>
                    <s.DetailBtn
                      onClick={() => setRegionModalOn(true)}
                      $active={!!investOptions.pnu}
                    >
                      {(investOptions.pnu &&
                        `${investOptions.region.sido} ${investOptions.region.sigungu} ${investOptions.region.emd}`) ||
                        '지역 (필수)'}
                    </s.DetailBtn>
                  </div>
                </s.BlockContent>
              </s.Block>
            </s.Column>
            <s.Column>
              <s.Block>
                <s.BlockLabel>
                  <span>투자 물건</span>
                </s.BlockLabel>
                <s.BlockContent>
                  <div style={{ width: '100%', display: 'flex', gap: '8px' }}>
                    <s.DetailBtn
                      onClick={() => setCategoryModalOn(true)}
                      $active={!!investOptions.category}
                    >
                      {investOptions.category || '물건 종류 (필수)'}
                    </s.DetailBtn>
                    <s.DetailBtn
                      onClick={() => {
                        if (!investOptions.category) return;
                        setDetailModalOn(true);
                      }}
                      $active={!!investOptions.categoryDetail}
                    >
                      {investOptions.categoryDetail || '상세 분류'}
                    </s.DetailBtn>
                  </div>
                </s.BlockContent>
              </s.Block>
            </s.Column>
            <s.Column>
              <s.Block>
                <s.BlockLabel>
                  <span>투자 기간</span>
                </s.BlockLabel>
                <s.BlockContent>
                  {investmentPeriodOptions.map((option) => (
                    <s.RadioButtonLabel02
                      key={option.value}
                      checked={investOptions.investmentPeriod === option.value}
                    >
                      <input
                        type="radio"
                        name="investment-period"
                        value={option.value}
                        checked={
                          investOptions.investmentPeriod === option.value
                        }
                        onChange={() => {
                          setInvestOptions((prev) => ({
                            ...prev,
                            investmentPeriod: option.value,
                          }));
                        }}
                        onClick={(e) => {
                          // 이미 선택된 걸 다시 클릭하면 -> A로 초기화
                          if (investOptions.investmentPeriod === option.value) {
                            e.preventDefault();
                            setInvestOptions((prev) => ({
                              ...prev,
                              investmentPeriod: 'A',
                            }));
                          }
                        }}
                      />
                      {option.label}
                      <span>{option.description}</span>
                    </s.RadioButtonLabel02>
                  ))}
                </s.BlockContent>
              </s.Block>
            </s.Column>
            <s.Column>
              <s.Block>
                <s.BlockLabel>
                  <span>투자 목적</span>
                </s.BlockLabel>
                <s.BlockContent02>
                  {investmentPurposeOptions
                    .filter((option) => option.value !== 'A')
                    .map((option) => (
                      <s.CheckButtonLabel
                        key={option.value}
                        onChange={() => togglePurpose(option.value)}
                      >
                        <input
                          type="checkbox"
                          checked={
                            investOptions.investmentPurpose === option.value
                          }
                        />
                        <img
                          src={
                            investOptions.investmentPurpose === option.value
                              ? '/assets/images/icon/algo-check-icon-on.svg'
                              : '/assets/images/icon/algo-check-icon-off.svg'
                          }
                          alt={option.label}
                        />
                        {option.label}
                      </s.CheckButtonLabel>
                    ))}
                </s.BlockContent02>
              </s.Block>
            </s.Column>

            {/* 노후년도는 항상 표시 */}
            {showAgingYear && (
              <s.Column>
                <s.Block style={{ flex: 0.5 }}>
                  <s.BlockLabel>
                    <span>노후년도</span>
                  </s.BlockLabel>
                  <s.BlockDescriptionSection>
                    <s.BlockContent>
                      <s.DetailBtn
                        onClick={() => setIsYearModalOpen(true)}
                        $active={
                          !!investOptions.aging_start_year ||
                          !!investOptions.aging_end_year
                        }
                      >
                        {!investOptions.aging_start_year &&
                        !investOptions.aging_end_year ? (
                          <span>노후년도 선택</span>
                        ) : (
                          <>
                            {getSubLabel([
                              Number(investOptions.aging_start_year) || 1980,
                              Number(investOptions.aging_end_year) || 2025,
                            ])}
                          </>
                        )}
                      </s.DetailBtn>
                    </s.BlockContent>
                    <s.InputDesc>
                      {getRangeLabel([
                        Number(investOptions.aging_start_year) || 1980,
                        Number(investOptions.aging_end_year) || 2025,
                      ])}
                    </s.InputDesc>
                  </s.BlockDescriptionSection>
                </s.Block>
              </s.Column>
            )}

            {showUsageAreaBlock && (
              <s.Column>
                <s.Block style={{ flex: 0.5 }}>
                  <s.BlockLabel>
                    <span>용도지역</span>
                  </s.BlockLabel>
                  <s.BlockContent>
                    <s.DetailBtn
                      onClick={() => setIsUseAreaModalOpen(true)}
                      $active={investOptions.useArea.length > 0}
                    >
                      {investOptions.useArea.length === 0 ? (
                        <span>용도지역 선택</span>
                      ) : (
                        <>
                          {investOptions.useArea.slice(0, 2).join(', ')}
                          {investOptions.useArea.length > 2 &&
                            ` +${investOptions.useArea.length - 2}`}
                        </>
                      )}
                    </s.DetailBtn>
                  </s.BlockContent>
                </s.Block>
              </s.Column>
            )}

            {regionModalOn && (
              <RegionSelectModal
                initialRegion={{
                  pnu: investOptions.pnu,
                  ...investOptions.region,
                }}
                sidoList={userSidoList}
                disableList={disableList}
                onSelectRegion={handleChangeRegion}
                onClose={() => setRegionModalOn(false)}
              />
            )}
            {categoryModalOn && (
              <ItemCategoryModal
                selected={investOptions.category}
                onApply={(val) => {
                  setInvestOptions((prev) => ({
                    ...prev,
                    category: val,
                    categoryDetail: '',
                  }));
                }}
                onClose={() => setCategoryModalOn(false)}
              />
            )}
            {detailModalOn && (
              <ItemDetailModal
                category={investOptions.category}
                selected={investOptions.categoryDetail}
                onApply={(val) => {
                  setInvestOptions((prev) => ({
                    ...prev,
                    categoryDetail: val,
                  }));
                }}
                onClose={() => setDetailModalOn(false)}
              />
            )}

            {isYearModalOpen && (
              <YearRangeSliderModal
                selected={{
                  start: investOptions.aging_start_year ?? '1980',
                  end: investOptions.aging_end_year ?? '2025',
                }}
                onApply={handleYearApply}
                onClose={() => setIsYearModalOpen(false)}
              />
            )}

            {isUseAreaModalOpen && (
              <UseAreaModal
                selected={investOptions.useArea}
                onApply={(selected) =>
                  setInvestOptions((prev) => ({ ...prev, useArea: selected }))
                }
                onClose={() => setIsUseAreaModalOpen(false)}
              />
            )}
          </s.SelectMain>
        </>
      )}
      <SelectedOptions tab="투자" />
    </s.PatternBox>
  );
};

export default AnalysisCriteria;
